<template>
  <div
    class="flex items-center justify-center"
    style="touch-action: none"
    @touchmove.stop.prevent>
    <svg
      ref="dialRef"
      :viewBox="dialViewBox"
      :width="dialSize"
      :height="dialSize"
      class="select-none"
      style="touch-action: none"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp">
      <!-- Background arc -->
      <path
        :d="bgArcPath"
        fill="none"
        stroke="currentColor"
        class="text-gray-700"
        :stroke-width="trackWidth" />

      <!-- Tick marks -->
      <template v-for="tick in ticks" :key="tick.value">
        <line
          :x1="tick.outerX"
          :y1="tick.outerY"
          :x2="tick.innerX"
          :y2="tick.innerY"
          :stroke="tick.major ? '#6b7280' : '#4b5563'"
          :stroke-width="tick.major ? 1.5 : 0.75" />
        <text
          v-if="tick.major"
          :x="tick.labelX"
          :y="tick.labelY"
          fill="#9ca3af"
          font-size="8"
          text-anchor="middle"
          dominant-baseline="central">
          {{ tick.value }}
        </text>
      </template>

      <!-- Value fill arc -->
      <path
        v-if="value > 0"
        :d="valueArcPath"
        fill="none"
        :stroke="color"
        :stroke-width="trackWidth"
        stroke-linecap="round" />

      <!-- Thumb dot on the arc -->
      <circle
        :cx="thumbPos.x"
        :cy="thumbPos.y"
        :r="isDragging ? 8 : 6"
        fill="white"
        :stroke="color"
        :stroke-width="2" />

      <!-- Center value display -->
      <text
        :x="cx"
        :y="cx - 2"
        fill="white"
        :font-size="20"
        font-weight="600"
        text-anchor="middle"
        dominant-baseline="central"
        class="tabular-nums">
        {{ value.toFixed(1) }}
      </text>
      <text
        :x="cx"
        :y="cx + 14"
        fill="#9ca3af"
        font-size="9"
        text-anchor="middle"
        dominant-baseline="central">
        {{ label }}
      </text>
    </svg>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  value: { type: Number, default: 5 },
  color: { type: String, default: '#6366f1' },
  label: { type: String, default: '' },
  dialSize: { type: Number, default: 160 }
})

const emit = defineEmits(['update:value'])

const size = 160
const PADDING = 6
const cx = size / 2
const cy = size / 2
const radius = 58
const trackWidth = 10

const dialViewBox = `${-PADDING} ${-PADDING} ${size + PADDING * 2} ${size + PADDING * 2}`

// Arc geometry: 270-degree arc, gap at bottom
// SVG angles: 0=right, 90=down, 180=left, 270=up
// Arc starts at SVG 135° (bottom-left), ends at SVG 45° (bottom-right), clockwise
const ARC_START_DEG = 135
const ARC_SPAN_DEG = 270

const dialRef = ref(null)
const isDragging = ref(false)
const activePointerId = ref(null)

const toRad = (deg) => (deg * Math.PI) / 180

const pointOnArc = (angleDeg, r = radius) => ({
  x: cx + r * Math.cos(toRad(angleDeg)),
  y: cy + r * Math.sin(toRad(angleDeg))
})

const valueToAngle = (v) =>
  ARC_START_DEG + (v / 10) * ARC_SPAN_DEG

const arcPath = (startAngle, endAngle, r = radius) => {
  const start = pointOnArc(startAngle, r)
  const end = pointOnArc(endAngle, r)
  const span = ((endAngle - startAngle) + 360) % 360
  const largeArc = span > 180 ? 1 : 0
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

const bgArcPath = computed(() =>
  arcPath(ARC_START_DEG, ARC_START_DEG + ARC_SPAN_DEG)
)

const valueArcPath = computed(() => {
  const endAngle = valueToAngle(props.value)
  return arcPath(ARC_START_DEG, endAngle)
})

const thumbPos = computed(() =>
  pointOnArc(valueToAngle(props.value))
)

// Tick marks at every integer (0-10) and half-integers
const ticks = computed(() => {
  const result = []
  for (let i = 0; i <= 20; i++) {
    const v = i * 0.5
    const angle = valueToAngle(v)
    const major = v === Math.floor(v)
    const outerR = radius + trackWidth / 2 + 2
    const innerR = radius + trackWidth / 2 + (major ? 8 : 5)
    const labelR = radius + trackWidth / 2 + 16
    const outer = pointOnArc(angle, outerR)
    const inner = pointOnArc(angle, innerR)
    const label = pointOnArc(angle, labelR)
    result.push({
      value: v,
      major,
      outerX: outer.x,
      outerY: outer.y,
      innerX: inner.x,
      innerY: inner.y,
      labelX: label.x,
      labelY: label.y
    })
  }
  return result
})

// Pointer event handling
const getValueFromPointer = (e) => {
  const svg = dialRef.value
  if (!svg) return null
  const rect = svg.getBoundingClientRect()
  const vbSize = size + PADDING * 2
  const scale = vbSize / rect.width
  const dx = (e.clientX - rect.left) * scale - PADDING - cx
  const dy = (e.clientY - rect.top) * scale - PADDING - cy

  // SVG angle from center to pointer
  let svgAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360

  // Normalize relative to arc start
  let normalized = ((svgAngle - ARC_START_DEG) + 360) % 360

  if (normalized > ARC_SPAN_DEG) {
    // In the dead zone — snap to nearest end
    const deadZoneMiddle = ARC_SPAN_DEG + (360 - ARC_SPAN_DEG) / 2
    return normalized > deadZoneMiddle ? 0 : 10
  }

  return Math.round((normalized / ARC_SPAN_DEG) * 10 * 10) / 10
}

const onPointerDown = (e) => {
  if (activePointerId.value !== null) return
  e.preventDefault()
  activePointerId.value = e.pointerId
  isDragging.value = true
  dialRef.value.setPointerCapture(e.pointerId)
  const v = getValueFromPointer(e)
  if (v !== null) emit('update:value', v)
}

const onPointerMove = (e) => {
  if (e.pointerId !== activePointerId.value) return
  e.preventDefault()
  const v = getValueFromPointer(e)
  if (v !== null) emit('update:value', v)
}

const onPointerUp = (e) => {
  if (e.pointerId !== activePointerId.value) return
  isDragging.value = false
  activePointerId.value = null
}
</script>
